import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Loader2, X, Check, AlertTriangle, RefreshCw, Upload } from "lucide-react";

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = String(result).split(",")[1] || String(result);
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Resize/compress image client-side before upload to avoid huge payloads
async function compressImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            resolve(file);
            return;
          }
          resolve(new File([blob], "scan.jpg", { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export default function ScanModal({ open, onClose, onResult }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {container_no, check_digit_valid}
  const [error, setError] = useState(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setPreviewUrl(null); setResult(null); setError(null); setLoading(false);
  };

  const handleFile = async (file) => {
    if (!file) return;
    reset();
    setLoading(true);
    try {
      const compressed = await compressImage(file);
      const url = URL.createObjectURL(compressed);
      setPreviewUrl(url);
      const b64 = await fileToBase64(compressed);
      const r = await api.post("/containers/scan", { image_base64: b64 });
      setResult(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Scan failed";
      const friendly =
        status === 402 ? "LLM credits exhausted. Top up your Universal Key in Profile → Universal Key → Add Balance."
        : status === 429 ? "OCR service is busy. Try again in a few seconds."
        : status === 422 ? "Couldn't read a container number. Try a closer, well-lit shot."
        : detail;
      setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  const useNumber = () => {
    if (result?.container_no) {
      onResult(result.container_no);
      handleClose();
    }
  };

  const handleClose = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" data-testid="scan-modal">
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">Scan Container</div>
            <div className="font-display font-extrabold text-lg text-[#0a2952] mt-1">Snap the container number</div>
          </div>
          <button type="button" onClick={handleClose} data-testid="scan-close-btn" className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Hidden inputs */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            data-testid="scan-camera-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="scan-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {/* Preview */}
          {previewUrl && (
            <div className="relative rounded-md overflow-hidden bg-slate-900 aspect-[4/3]">
              <img src={previewUrl} alt="Scan preview" className="w-full h-full object-cover" />
              {loading && (
                <div className="absolute inset-0 grid place-items-center bg-black/50 backdrop-blur-sm" data-testid="scan-loading">
                  <div className="flex flex-col items-center gap-2 text-white">
                    <Loader2 className="animate-spin" size={28} />
                    <div className="text-sm font-semibold">Reading container number…</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && !loading && (
            <div className="bg-[#0a2952]/5 border border-[#0a2952]/10 rounded-md p-4" data-testid="scan-result">
              <div className="overline mb-2 text-[#0a2952]">Detected</div>
              <div className="font-mono-tabular font-bold text-3xl text-[#0a2952] tracking-wider mb-2">
                {result.container_no}
              </div>
              {result.check_digit_valid ? (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#15803d]" data-testid="scan-check-valid">
                  <Check size={14} /> Check digit valid (ISO 6346)
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700" data-testid="scan-check-invalid">
                  <AlertTriangle size={14} /> Check digit may be wrong — please verify
                </div>
              )}
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700" data-testid="scan-error">
              <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle size={14} /> Couldn't read it</div>
              <div className="text-xs">{error}. Try a closer, sharper, well-lit shot of the printed number.</div>
            </div>
          )}

          {!previewUrl && !loading && (
            <div className="text-center py-6 text-slate-500 text-sm">
              <Camera size={36} className="mx-auto mb-3 text-slate-300" />
              <div className="font-semibold text-[#0a2952]">Point your camera at the container ID</div>
              <div className="text-xs mt-1">It works best with the full 11-char code centered and in focus.</div>
            </div>
          )}

          {/* Actions */}
          {!previewUrl && !loading && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                data-testid="scan-take-photo-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] text-white py-3 rounded-md font-semibold transition-all hover:-translate-y-[1px]"
              >
                <Camera size={16} /> Take photo
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="scan-upload-btn"
                className="inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-[#0a2952] text-slate-700 py-3 rounded-md font-semibold"
              >
                <Upload size={16} /> Upload
              </button>
            </div>
          )}

          {(result || error) && !loading && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={reset}
                data-testid="scan-retry-btn"
                className="inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-[#0a2952] text-slate-700 py-3 rounded-md font-semibold"
              >
                <RefreshCw size={14} /> Try again
              </button>
              <button
                type="button"
                onClick={useNumber}
                disabled={!result?.container_no}
                data-testid="scan-use-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 rounded-md font-semibold transition-all"
              >
                <Check size={16} /> Use this number
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
